/**
 * FeedContext.jsx — PRODUCTION v4
 * Place at: src/context/FeedContext.jsx
 *
 * BUGS FIXED vs v3:
 *
 *   BUG 1 — castVote downvote scoreDelta always 0
 *     All 3 vote transition branches had 0 for downvote — UI score never changed
 *     on downvote. Fixed with correct delta for all 6 transitions:
 *     toggle-off up, toggle-off down, null→up, null→down, up→down, down→up.
 *
 *   BUG 2 — notifFetchedRef never reset on user change
 *     If user A logs out and user B logs in same session, notifFetchedRef stayed
 *     true → B saw A's cached notifications until a tab-return fired.
 *     Fixed: reset notifFetchedRef whenever currentUser.id changes.
 *
 *   BUG 3 — AbortSignal not passed to parallel queries in following/bookmarks tabs
 *     The main RPC aborted correctly but the 3 parallel hydration queries
 *     in the following tab kept running. Fixed: .abortSignal(signal) on each.
 *     (bookmarks tab: same fix applied)
 *
 * OPTIMIZATIONS ADDED:
 *
 *   OPT 1 — Feed-level 5-minute cache (feedCacheRef)
 *     Keyed by "tab|sort|tag|search". Tab switching back within 5 min
 *     restores from cache instantly — zero Supabase query.
 *     Cache is invalidated on: new post submitted (addPost), filter change.
 *     UX: feed feels instant on back-navigation. "New posts" banner shown
 *     if cache is stale so users know they can refresh.
 *
 *   OPT 2 — following tab now calls upgraded get_following_posts RPC
 *     The 3 parallel Promise.all hydration queries are removed.
 *     The new RPC returns my_vote/is_bookmarked/is_reported directly.
 *     (SQL change in SQL-PRODUCTION.sql — same data, 1 query instead of 4)
 *
 *   OPT 3 — bookmarks tab now calls new get_bookmarked_posts RPC
 *     1 query instead of 3.
 *
 *   OPT 4 — newPostsBanner state
 *     When feed cache is served (stale but valid), a banner appears:
 *     "New posts available — tap to refresh". Zero DB cost.
 *     Disappears as soon as user refreshes. Keeps feed feeling alive.
 */

import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { supabase } from "../supabase";

const FeedContext = createContext(null);

export function useFeedContext() {
  const ctx = useContext(FeedContext);
  if (!ctx) throw new Error("useFeedContext must be used inside FeedProvider");
  return ctx;
}

const PAGE_SIZE       = 10;
const MAX_MAP_SIZE    = 200;   // evict voteMap/bookmarkMap/reportMap entries above this
const FEED_CACHE_TTL  = 5 * 60 * 1000; // 5 minutes — matches comment cache extension

// ─── PROVIDER ─────────────────────────────────────────────────────────────────
export function FeedProvider({ children, currentUser }) {

  const [posts,          setPosts]          = useState([]);
  const [voteMap,        setVoteMap]        = useState({});
  const [bookmarkMap,    setBookmarkMap]    = useState({});
  const [reportMap,      setReportMap]      = useState({});

  const [notifCache,     setNotifCache]     = useState([]);
  const [unreadCount,    setUnreadCount]    = useState(0);
  const notifFetchedRef  = useRef(false);
  const lastNotifFetchAt = useRef(0); // throttle visibilitychange fetches

  const [feedTab,        setFeedTabState]   = useState("all");
  const [sortBy,         setSortByState]    = useState("hot");
  const [activeTag,      setActiveTagState] = useState(null);
  const [searchQuery,    setSearchQueryState] = useState("");
  const [loadingPosts,   setLoadingPosts]   = useState(false);
  const [hasMore,        setHasMore]        = useState(true);
  const [fetchError,     setFetchError]     = useState(null);
  const [newPostsBanner, setNewPostsBanner] = useState(false); // OPT 4

  const loadingRef   = useRef(false);
  const pageRef      = useRef(0);
  const abortCtrlRef = useRef(null);

  // OPT 1 — Feed-level cache
  // Map key: "tab|sort|tag|search"
  // Map value: { posts, voteMap, bookmarkMap, reportMap, pageRef, hasMore, fetchedAt }
  const feedCacheRef = useRef(new Map());

  // ─── BUG 2 FIX: Reset notif state when user changes ────────────────────────
  const prevUserIdRef = useRef(null);
  useEffect(() => {
    if (currentUser?.id && currentUser.id !== prevUserIdRef.current) {
      prevUserIdRef.current    = currentUser.id;
      notifFetchedRef.current  = false;
      lastNotifFetchAt.current = 0;
      setNotifCache([]);
      setUnreadCount(0);
      // Clear feed cache so new user never sees previous user's vote/bookmark state
      feedCacheRef.current.clear();
    }
  }, [currentUser?.id]);

  // ─── HELPERS ────────────────────────────────────────────────────────────────

  const evictStaleMapEntries = useCallback((map, currentPostIds) => {
    const keys = Object.keys(map);
    if (keys.length <= MAX_MAP_SIZE) return map;
    const activeSet = new Set(currentPostIds);
    const pruned = {};
    keys.forEach(k => { if (activeSet.has(k)) pruned[k] = map[k]; });
    return pruned;
  }, []);

  const mergeOrSetMaps = useCallback((voteM, bookmarkM, reportM, reset, currentPostIds = []) => {
    if (reset) {
      setVoteMap(voteM);
      setBookmarkMap(bookmarkM);
      setReportMap(reportM);
    } else {
      setVoteMap(prev => evictStaleMapEntries({ ...prev, ...voteM }, currentPostIds));
      setBookmarkMap(prev => evictStaleMapEntries({ ...prev, ...bookmarkM }, currentPostIds));
      setReportMap(prev => evictStaleMapEntries({ ...prev, ...reportM }, currentPostIds));
    }
  }, [evictStaleMapEntries]);

  const extractMaps = useCallback((rows) => {
    const voteM = {}, bookmarkM = {}, reportM = {};
    rows.forEach(p => {
      if (p.my_vote)       voteM[p.id]     = p.my_vote;
      if (p.is_bookmarked) bookmarkM[p.id] = true;
      if (p.is_reported)   reportM[p.id]   = true;
    });
    return { voteM, bookmarkM, reportM };
  }, []);

  const getCacheKey = useCallback((tab, sort, tag, search) => {
    return `${tab}|${sort}|${tag ?? ""}|${search ?? ""}`;
  }, []);

  // ─── FETCH FEED ─────────────────────────────────────────────────────────────
  const fetchFeed = useCallback(async (reset = false, overrides = {}) => {
    if (!currentUser) return;
    if (loadingRef.current && !reset) return;

    // Abort any in-flight previous fetch
    if (abortCtrlRef.current) abortCtrlRef.current.abort();
    const controller = new AbortController();
    abortCtrlRef.current = controller;
    const { signal } = controller;

    const tab    = overrides.feedTab     ?? feedTab;
    const sort   = overrides.sortBy      ?? sortBy;
    const tag    = "activeTag"    in overrides ? overrides.activeTag    : activeTag;
    const search = "searchQuery"  in overrides ? overrides.searchQuery  : searchQuery;

    // OPT 1 — check feed cache on reset (tab switch / filter change)
    if (reset) {
      const cacheKey = getCacheKey(tab, sort, tag, search);
      const cached   = feedCacheRef.current.get(cacheKey);
      if (cached && Date.now() - cached.fetchedAt < FEED_CACHE_TTL) {
        // Restore from cache instantly — zero DB query
        // FIX 1: restore actual maps (not empty objects) so vote/bookmark state is preserved
        // FIX 2: reset loadingRef so infinite scroll isn't blocked after a cache hit
        loadingRef.current = false;
        setPosts(cached.posts);
        setVoteMap(cached.voteMap);
        setBookmarkMap(cached.bookmarkMap);
        setReportMap(cached.reportMap);
        setHasMore(cached.hasMore);
        pageRef.current = cached.pageRef;
        setNewPostsBanner(true); // OPT 4 — tell user there may be newer posts
        return;
      }
      setNewPostsBanner(false);
    }

    loadingRef.current = true;
    setLoadingPosts(true);
    setFetchError(null);

    const currentPage = reset ? 0 : pageRef.current;
    const from        = currentPage * PAGE_SIZE;
    const to          = from + PAGE_SIZE - 1;

    try {
      let rows = [];

      if (tab === "following") {
        // OPT 2 — 1 query (upgraded RPC includes user state via LEFT JOINs)
        const { data, error } = await supabase.rpc("get_following_posts", {
          p_user_id: currentUser.id,
          p_from:    from,
          p_to:      to,
        }, { signal });
        if (error) throw error;
        rows = data || [];
        const { voteM, bookmarkM, reportM } = extractMaps(rows);
        mergeOrSetMaps(voteM, bookmarkM, reportM, reset, rows.map(p => p.id));

      } else if (tab === "mine") {
        const { data, error } = await supabase.rpc("get_feed_with_user_state", {
          p_user_id:   currentUser.id,
          p_from:      from,
          p_to:        to,
          p_tag:       tag    || null,
          p_search:    search || null,
          p_sort:      sort,
          p_author_id: currentUser.id,
        }, { signal });
        if (error) throw error;
        rows = data || [];
        const { voteM, bookmarkM, reportM } = extractMaps(rows);
        mergeOrSetMaps(voteM, bookmarkM, reportM, reset, rows.map(p => p.id));

      } else if (tab === "bookmarks") {
        // OPT 3 — 1 query (new get_bookmarked_posts RPC)
        const { data, error } = await supabase.rpc("get_bookmarked_posts", {
          p_user_id: currentUser.id,
          p_from:    from,
          p_to:      to,
        }, { signal });
        if (error) throw error;
        rows = (data || []).filter(Boolean);
        const voteM = {}, bookmarkM = {}, reportM = {};
        rows.forEach(p => {
          bookmarkM[p.id] = true;
          if (p.my_vote)     voteM[p.id]    = p.my_vote;
          if (p.is_reported) reportM[p.id]  = true;
        });
        mergeOrSetMaps(voteM, bookmarkM, reportM, reset, rows.map(p => p.id));

      } else {
        // "all" tab — single enriched RPC
        const { data, error } = await supabase.rpc("get_feed_with_user_state", {
          p_user_id:   currentUser.id,
          p_from:      from,
          p_to:        to,
          p_tag:       tag    || null,
          p_search:    search || null,
          p_sort:      sort,
          p_author_id: null,
        }, { signal });
        if (error) throw error;
        rows = data || [];
        const { voteM, bookmarkM, reportM } = extractMaps(rows);
        mergeOrSetMaps(voteM, bookmarkM, reportM, reset, rows.map(p => p.id));
      }

      if (signal.aborted) return;

      const visible = rows.filter(p => !p.is_deleted && !p.is_hidden);
      const more    = rows.length === PAGE_SIZE;
      setHasMore(more);

      if (reset) {
        setPosts(visible);
        pageRef.current = 1;

        // OPT 1 — store in feed cache after successful fetch.
        // BUG FIX: use extractMaps on visible rows for this page.
        // On a reset (page 0), this captures all state for the first page.
        // The cache is intentionally only written on reset=true (page 0)
        // because we want the cache to represent a full "tab state" snapshot,
        // not a partial scroll-down state. If the user scrolled to page 3
        // and switches tabs, the cache will only have page-0 data — which
        // means on restore they see page 0 again. This is correct UX:
        // the "New posts available" banner appears so they can refresh to
        // see everything fresh. Storing partial scroll state would be worse
        // (inconsistent vote state, missing posts, confusing ordering).
        const cacheKey = getCacheKey(tab, sort, tag, search);
        const { voteM: cacheVoteM, bookmarkM: cacheBkM, reportM: cacheRpM } = extractMaps(visible);
        feedCacheRef.current.set(cacheKey, {
          posts:       visible,
          voteMap:     cacheVoteM,
          bookmarkMap: cacheBkM,
          reportMap:   cacheRpM,
          hasMore:     more,
          pageRef:     1,
          fetchedAt:   Date.now(),
        });
        // Evict old cache entries (keep max 10 combinations)
        if (feedCacheRef.current.size > 10) {
          const firstKey = feedCacheRef.current.keys().next().value;
          feedCacheRef.current.delete(firstKey);
        }
      } else {
        setPosts(prev => [...prev, ...visible]);
        pageRef.current = currentPage + 1;
      }

    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("fetchFeed error:", err);
      setFetchError("Failed to load posts. Please try again.");
      if (!reset) pageRef.current = currentPage; // FIX C rollback
    } finally {
      if (abortCtrlRef.current === controller) {
        loadingRef.current = false;
        setLoadingPosts(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, feedTab, sortBy, activeTag, searchQuery, mergeOrSetMaps, extractMaps, getCacheKey]);

  // ─── PAGINATION ──────────────────────────────────────────────────────────────
  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    fetchFeed(false);
  }, [fetchFeed, hasMore]);

  // ─── POST MUTATIONS ──────────────────────────────────────────────────────────
  const addPost = useCallback((post) => {
    setPosts(prev => [post, ...prev]);
    // Invalidate all feed caches so next tab switch fetches fresh data
    feedCacheRef.current.clear();
    setNewPostsBanner(false);
  }, []);

  const removePost = useCallback((postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    setVoteMap(prev     => { const n = { ...prev }; delete n[postId]; return n; });
    setBookmarkMap(prev => { const n = { ...prev }; delete n[postId]; return n; });
    setReportMap(prev   => { const n = { ...prev }; delete n[postId]; return n; });
    // FIX: invalidate feed cache so deleted post doesn't reappear on tab switch
    feedCacheRef.current.clear();
  }, []);

  const updatePost = useCallback((postId, updates) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, ...updates } : p));
  }, []);

  // ─── OPTIMISTIC VOTE ─────────────────────────────────────────────────────────
  // BUG 1 FIX — all 6 vote transition deltas now correct.
  // This tracks changes to post.upvotes (displayed as reaction count).
  // The DB atomic_vote RPC handles downvotes separately in posts.downvotes.
  const castVote = useCallback(async (postId, type) => {
    if (!currentUser) return;

    const previousVote = voteMap[postId] ?? null;
    const isToggleOff  = previousVote === type;
    const newVote      = isToggleOff ? null : type;

    // Correct delta for all 6 transitions:
    // toggle-off up:   -1  (removing an upvote)
    // toggle-off down: +1  (removing a downvote raises net score)
    // null → up:       +1
    // null → down:     -1
    // up   → down:     -2  (lose +1, gain -1)
    // down → up:       +2  (lose -1, gain +1)
    let scoreDelta = 0;
    if (isToggleOff) {
      scoreDelta = type === "up" ? -1 : +1;
    } else if (previousVote === null) {
      scoreDelta = type === "up" ? +1 : -1;
    } else {
      // switching vote
      scoreDelta = type === "up" ? +2 : -2;
    }

    // Optimistic update
    setVoteMap(prev => {
      const next = { ...prev };
      if (newVote === null) delete next[postId];
      else next[postId] = newVote;
      return next;
    });
    if (scoreDelta !== 0) {
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, upvotes: Math.max(0, (p.upvotes || 0) + scoreDelta) } : p
      ));
    }

    try {
      const { error } = await supabase.rpc("atomic_vote", {
        p_post_id:       postId,
        p_user_id:       currentUser.id,
        p_vote_type:     isToggleOff ? "remove" : type,
        p_previous_vote: previousVote,
      });
      if (error) throw error;
      // FIX: keep cache entries in sync with the vote change
      // so tab-switch within 5 min doesn't revert the reaction
      feedCacheRef.current.forEach((entry, key) => {
        if (entry.posts.some(p => p.id === postId)) {
          const updatedVoteMap = { ...entry.voteMap };
          if (newVote === null) delete updatedVoteMap[postId];
          else updatedVoteMap[postId] = newVote;
          feedCacheRef.current.set(key, { ...entry, voteMap: updatedVoteMap });
        }
      });
    } catch (err) {
      console.error("castVote RPC failed, reverting:", err);
      // Rollback
      setVoteMap(prev => {
        const next = { ...prev };
        if (previousVote === null) delete next[postId];
        else next[postId] = previousVote;
        return next;
      });
      if (scoreDelta !== 0) {
        setPosts(prev => prev.map(p =>
          p.id === postId ? { ...p, upvotes: (p.upvotes || 0) - scoreDelta } : p
        ));
      }
    }
  }, [currentUser, voteMap]);

  // ─── OPTIMISTIC BOOKMARK ─────────────────────────────────────────────────────
  const toggleBookmark = useCallback(async (postId) => {
    if (!currentUser) return;
    const was = bookmarkMap[postId] ?? false;
    setBookmarkMap(prev => ({ ...prev, [postId]: !was }));
    try {
      if (was) {
        const { error } = await supabase.from("bookmarks")
          .delete().eq("user_id", currentUser.id).eq("post_id", postId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("bookmarks")
          .upsert({ user_id: currentUser.id, post_id: postId });
        if (error) throw error;
      }
      // FIX: keep cache entries in sync with bookmark change
      // so tab-switch within 5 min doesn't revert the bookmark state
      feedCacheRef.current.forEach((entry, key) => {
        if (entry.posts.some(p => p.id === postId)) {
          const updatedBookmarkMap = { ...entry.bookmarkMap };
          if (was) delete updatedBookmarkMap[postId];
          else updatedBookmarkMap[postId] = true;
          feedCacheRef.current.set(key, { ...entry, bookmarkMap: updatedBookmarkMap });
        }
      });
    } catch (err) {
      console.error("toggleBookmark failed, reverting:", err);
      setBookmarkMap(prev => ({ ...prev, [postId]: was }));
    }
  }, [currentUser, bookmarkMap]);

  // ─── NOTIFICATIONS ────────────────────────────────────────────────────────────
  const addNotif = useCallback((notif) => {
    setNotifCache(prev => {
      if (prev.find(n => n.id === notif.id)) return prev;
      return [notif, ...prev];
    });
    if (!notif.is_read) setUnreadCount(c => c + 1);
  }, []);

  const bulkSetNotifs = useCallback((notifs) => {
    const seen    = new Set();
    const deduped = notifs.filter(n => {
      if (seen.has(n.id)) return false;
      seen.add(n.id);
      return true;
    });
    setNotifCache(deduped);
    setUnreadCount(deduped.filter(n => !n.is_read).length);
    lastNotifFetchAt.current = Date.now();
  }, []);

  const markNotifsRead = useCallback(async () => {
    if (!currentUser || unreadCount === 0) return;
    setUnreadCount(0);
    setNotifCache(prev => prev.map(n => ({ ...n, is_read: true })));
    await supabase.from("notifications")
      .update({ is_read: true })
      .eq("recipient_id", currentUser.id)
      .eq("is_read", false);
  }, [currentUser, unreadCount]);

  // ─── FILTER SETTERS ──────────────────────────────────────────────────────────
  const setFeedTab = useCallback((tab) => {
    setFeedTabState(tab);
    pageRef.current = 0;
    setPosts([]);
    fetchFeed(true, { feedTab: tab });
  }, [fetchFeed]);

  const setSortBy = useCallback((sort) => {
    setSortByState(sort);
    pageRef.current = 0;
    setPosts([]);
    feedCacheRef.current.clear(); // sort change invalidates all caches
    fetchFeed(true, { sortBy: sort });
  }, [fetchFeed]);

  const setActiveTag = useCallback((tag) => {
    setActiveTagState(tag);
    pageRef.current = 0;
    setPosts([]);
    fetchFeed(true, { activeTag: tag });
  }, [fetchFeed]);

  const setSearchQuery = useCallback((q) => {
    setSearchQueryState(q);
    pageRef.current = 0;
    setPosts([]);
    fetchFeed(true, { searchQuery: q });
  }, [fetchFeed]);

  const dismissNewPostsBanner = useCallback(() => {
    setNewPostsBanner(false);
    // Don't call setPosts([]) here — fetchFeed(reset=true) replaces posts atomically.
    // Calling setPosts([]) first causes a visible empty-state flash before fetch resolves.
    feedCacheRef.current.clear();
    fetchFeed(true);
  }, [fetchFeed]);

  // ─── MEMOIZED CONTEXT VALUE ──────────────────────────────────────────────────
  const value = useMemo(() => ({
    posts, voteMap, bookmarkMap, reportMap,
    notifCache, unreadCount, fetchError,
    loadingPosts, hasMore,
    feedTab, sortBy, activeTag, searchQuery,
    newPostsBanner,
    fetchFeed, loadMore,
    addPost, removePost, updatePost,
    castVote, toggleBookmark,
    setVoteMap, setBookmarkMap, setReportMap,
    addNotif, bulkSetNotifs, markNotifsRead, setUnreadCount,
    notifFetchedRef, lastNotifFetchAt,
    setFeedTab, setSortBy, setActiveTag, setSearchQuery,
    dismissNewPostsBanner,
  }), [
    posts, voteMap, bookmarkMap, reportMap,
    notifCache, unreadCount, fetchError,
    loadingPosts, hasMore,
    feedTab, sortBy, activeTag, searchQuery,
    newPostsBanner,
    fetchFeed, loadMore,
    addPost, removePost, updatePost,
    castVote, toggleBookmark,
    setVoteMap, setBookmarkMap, setReportMap,
    addNotif, bulkSetNotifs, markNotifsRead, setUnreadCount,
    setFeedTab, setSortBy, setActiveTag, setSearchQuery,
    dismissNewPostsBanner,
  ]);

  return (
    <FeedContext.Provider value={value}>
      {children}
    </FeedContext.Provider>
  );
}